import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { OwnerEmailChip } from "./DashboardParts";
import { Building2, Globe, Phone, Trash2, Search, MapPin, CreditCard, UserRound, Loader2, Download, Send, Check, Mail, Navigation } from "lucide-react";
import { RepEmailDialog } from "./RepEmailDialog";
import { exportBigCsv } from "../lib/exportCsv";
import { api, formatApiError } from "../lib/api";
import { US_STATES } from "../lib/constants";
import { StatesPicker } from "./AlertStatesPicker";
import { WhereToNext } from "./WhereToNext";
import { Input } from "./ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";

const CardBtn = ({ onClick, icon: Icon, children, testid, done }) => (
  <button onClick={onClick} data-testid={testid}
    className={`h-9 px-4 rounded-full text-sm font-semibold flex items-center gap-1.5 border transition-colors ${done ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "border-slate-200 text-blue-700 hover:bg-blue-700 hover:text-white hover:border-blue-700"}`}>
    <Icon className="h-4 w-4" />{children}
  </button>
);

const LEGACY_POS = ["NCR Aloha", "Focus POS", "Oracle Micros"];

const GroupCard = ({ g, onDelete, onToLeads, onSend, isAdmin, myId }) => {
  const [moving, setMoving] = useState(false);
  return (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 fade-up" data-testid={`big-group-${g.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="font-display text-lg font-bold text-slate-900">{g.name}</h3>
        {LEGACY_POS.includes(g.current_pos) && (
          <span className="inline-block mt-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-800 border border-orange-300" data-testid={`big-legacy-${g.id}`}>
            Older POS: {g.current_pos}
          </span>
        )}
        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="h-3.5 w-3.5" />{[g.hq_city, US_STATES[g.state]].filter(Boolean).join(", ")}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-sm font-extrabold px-3 py-1 rounded-full bg-blue-700 text-white" data-testid={`big-locations-${g.id}`}>{g.locations} locations</span>
        {isAdmin && <button onClick={() => onDelete(g)} aria-label="Remove" className="h-8 w-8 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors" data-testid={`big-delete-${g.id}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
    </div>
    {g.description && <p className="text-sm text-slate-700 mt-3" data-testid={`big-description-${g.id}`}>{g.description}</p>}
    {g.location_cities?.length > 0 && <p className="text-xs text-slate-500 mt-2">Locations: {g.location_cities.join(", ")}</p>}
    <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-sm">
      {g.website && <a href={g.website} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline flex items-center gap-1.5" data-testid={`big-website-${g.id}`}><Globe className="h-4 w-4" />{g.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}</a>}
      {g.phone && <a href={`tel:${g.phone}`} className="text-slate-700 flex items-center gap-1.5"><Phone className="h-4 w-4" />{g.phone}</a>}
      <span className="text-slate-700 flex items-center gap-1.5"><CreditCard className="h-4 w-4" />POS: <b>{g.current_pos || "Unknown"}</b></span>
      {g.contact_name && <span className="text-slate-700 flex items-center gap-1.5"><UserRound className="h-4 w-4" />{g.contact_name}{g.contact_title ? ` · ${g.contact_title}` : ""}</span>}
      {g.contact_phone && g.contact_phone !== g.phone && <a href={`tel:${g.contact_phone}`} className="text-blue-700 flex items-center gap-1.5" data-testid={`big-owner-phone-${g.id}`}><Phone className="h-4 w-4" />Owner: {g.contact_phone}</a>}
      {(g.contact_email || g.email) && <a href={`mailto:${g.contact_email || g.email}`} className="text-blue-700 hover:underline flex items-center gap-1.5" data-testid={`big-email-${g.id}`}><Mail className="h-4 w-4" />{g.contact_email || g.email}</a>}
      {g.source_url && <a href={g.source_url} target="_blank" rel="noreferrer" className="text-slate-500 hover:underline">Source</a>}
    </div>
    {isAdmin && <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
      <CardBtn onClick={() => setMoving((m) => !m)} icon={g.lead_id ? Check : Navigation} done={!!g.lead_id && !moving} testid={`big-to-leads-${g.id}`}>{g.lead_id ? "Copied to leads · Where to Next?" : "Where to Next?"}</CardBtn>
      <CardBtn onClick={() => onSend(g)} icon={Send} testid={`big-send-rep-${g.id}`}>Email to rep</CardBtn>
    </div>}
    {isAdmin && moving && <WhereToNext lead={{ id: g.id }} leadState={g.state} current="" isAdmin myId={myId} testid={`big-wtn-${g.id}`} onGo={async (m) => { if (await onToLeads(g, m)) setMoving(false); }} />}
  </div>
  );
};

export const BigPanel = ({ team, goToTeam, senderName, onLeadsChanged, role, allowedStates, myId }) => {
  const isAdmin = role === "admin";
  const isDemo = role === "demo";
  const [sending, setSending] = useState(null);
  const [groups, setGroups] = useState([]);
  const [scans, setScans] = useState([]);
  const [states, setStates] = useState([]);
  const [q, setQ] = useState("");
  const [ownerEmail, setOwnerEmail] = useState(false);
  const [legacy, setLegacy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const running = scans.some((s) => s.status === "running");

  const load = useCallback(() => {
    api.get("/big", { params: { q: q || undefined, owner_email: ownerEmail ? "yes" : undefined, legacy: legacy ? "yes" : undefined } }).then((r) => setGroups(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (!isDemo) api.get("/big/scans").then((r) => setScans(r.data)).catch((e) => console.warn("big scans", e));
  }, [q, isDemo, ownerEmail, legacy]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { if (!running) return; const t = setInterval(load, 5000); return () => clearInterval(t); }, [running, load]);

  const start = async () => {
    try {
      await api.post("/big/scan", { states, min_locations: 2 });
      toast.success(`Searching ${states.length} state${states.length === 1 ? "" : "s"} for restaurant groups. This takes about a minute per state.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toLeads = async (g, m) => {
    try {
      const { data } = await api.post(`/big/${g.id}/to-leads`, { bucket: m.bucket, reason: m.reason, note: m.note, for_uid: m.for_uid || null, jeff_note: m.jeff_note || null });
      const where = { future: "Future Leads", other: "Around Me", potential: "Potential", signed: "Signed", archived: "Dead deals" }[m.bucket] || "All leads";
      toast.success(`${g.name} moved to ${m.forName ? `${m.forName}'s ` : ""}${where}${m.bucket === "signed" ? (data.rep_emailed ? ` · ${m.forName} has been emailed` : ` · ${m.forName} has no email on file`) : ""}`);
      load();
      onLeadsChanged?.();
      return true;
    } catch (e) { toast.error(formatApiError(e)); return false; }
  };
  const remove = async (g) => {
    setToDelete(null);
    await api.delete(`/big/${g.id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  return (
    <div className="space-y-6" data-testid="big-section">
      {isDemo ? (
        <div className="rounded-2xl bg-slate-900 text-white p-6" data-testid="big-demo-note">
          <h2 className="font-display text-2xl font-extrabold">Restaurant groups</h2>
          <p className="text-sm text-slate-300 mt-1">Demo preview: one Texas group. Full accounts can search any state.</p>
        </div>
      ) : (
      <div className="rounded-3xl bg-slate-900 text-white p-6 sm:p-8 space-y-5 fade-up">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold">Multi Location Restaurant Finder</h2>
          <p className="text-sm text-slate-300 mt-1">Restaurants with 2 or more locations, a good website, and not on Toast.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <StatesPicker value={states} choices={allowedStates?.length ? allowedStates : undefined} onSave={async (s) => setStates(s)} title="States to search" note="Up to 10 states per search." emptyNote="Pick at least one state" testid="big-states-popover"
            trigger={<button className="h-10 px-4 rounded-full bg-white/10 hover:bg-white/20 text-sm font-semibold transition-colors" data-testid="big-states-button">{states.length ? states.join(", ") : "Pick states"}</button>} />
        </div>
        <button onClick={start} disabled={!states.length || states.length > 10 || running} data-testid="big-find-button"
          className="w-full sm:w-auto h-16 px-10 rounded-2xl bg-green-300 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed text-green-950 font-display text-xl font-extrabold tracking-wide flex items-center justify-center gap-3 transition-colors active:scale-[0.98]">
          {running ? <><Loader2 className="h-6 w-6 animate-spin" />Searching…</> : <><Building2 className="h-6 w-6" />BIG search</>}
        </button>
        {scans[0] && <p className="text-xs text-slate-400" data-testid="big-last-scan">Last search: {scans[0].states.join(", ")} · {scans[0].status} · {scans[0].found} found, {scans[0].new} new{scans[0].error ? ` · ${scans[0].error}` : ""}</p>}
      </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search groups, cities, cuisine…" className="pl-9 rounded-full w-[280px] bg-white" data-testid="big-search-input" />
        </div>
        <div className="flex flex-wrap gap-2">
          <OwnerEmailChip active={ownerEmail} onToggle={() => setOwnerEmail((v) => !v)} testid="big-owner-email-filter" />
          <button onClick={() => setLegacy((v) => !v)} aria-pressed={legacy} data-testid="big-legacy-filter"
            className={`h-10 px-5 rounded-full text-sm font-semibold border transition-colors ${legacy ? "bg-orange-600 border-orange-600 text-white" : "bg-white border-slate-200 text-slate-800 hover:border-orange-400"}`}>
            Older POS only
          </button>
        </div>
        <span className="text-sm text-slate-500" data-testid="big-count">{groups.length} group{groups.length === 1 ? "" : "s"}</span>
        {groups.length > 0 && !isDemo && <button onClick={() => exportBigCsv(groups).then(() => toast.success(`Exported ${groups.length} groups to Excel`)).catch((e) => toast.error(`Export failed: ${e.message}`))} data-testid="big-export-button"
          className="ml-auto h-10 px-5 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-800 hover:border-blue-400 flex items-center gap-2 transition-colors">
          <Download className="h-4 w-4" />Export to Excel
        </button>}
      </div>
      {!groups.length ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500" data-testid="big-empty">No restaurant groups yet. Pick states and tap BIG search.</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">{groups.map((g) => <GroupCard key={g.id} g={g} onDelete={setToDelete} onToLeads={toLeads} onSend={setSending} isAdmin={isAdmin} myId={myId} />)}</div>
      )}
      <RepEmailDialog lead={sending} onClose={() => setSending(null)} team={team} goToTeam={goToTeam} senderName={senderName}
        url={sending ? `/big/${sending.id}/send-rep` : undefined} note="It's also added to All leads and assigned to the first rep you pick."
        onSent={() => { load(); onLeadsChanged?.(); }} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="big-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>It will be taken off the BIG list. A later search may find it again.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="big-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove(toDelete)} className="bg-red-600 hover:bg-red-700" data-testid="big-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
